import React from 'react';
import moment from 'moment';
import { AuditShiftActivityItem } from 'models/audit/AuditDetailModel';
import { padLeftNumeric, parseBarcodeFormat } from 'utils/barcodeFormat';
import '../../style/ShiftActivityLedger.scss';

const PHONE_DETECTION_BREAK = '\u200C';

const formatActivityTime = (activity: AuditShiftActivityItem) => {
  if (activity.shift_date && activity.shift_time) {
    const parsed = moment(`${activity.shift_date}T${activity.shift_time}`);
    if (parsed.isValid()) {
      return parsed.format('M/D h:mm A');
    }
  }

  const parsed = moment.parseZone(activity.created_at);
  return parsed.isValid() ? parsed.format('M/D h:mm A') : 'Time unavailable';
};

const formatActivityLabel = (activity: AuditShiftActivityItem) => {
  if (activity.event_type === 'pack_loaded') {
    return 'Pack loaded';
  }

  if (activity.event_type === 'pack_removed') {
    return activity.removed_type === 'sold_out' ? 'Pack sold out' : 'Pack removed';
  }

  return 'Pack activity';
};

const formatGamePackValue = (
  activity: AuditShiftActivityItem,
  barcodeFormat?: string | null
) => {
  if (!activity.game_id || !activity.pack_id) {
    return '';
  }

  try {
    const pattern = parseBarcodeFormat(barcodeFormat);
    const game = padLeftNumeric(activity.game_id, pattern.game.length).slice(-pattern.game.length);
    const pack = padLeftNumeric(activity.pack_id, pattern.pack.length).slice(-pattern.pack.length);
    return `${game}-${PHONE_DETECTION_BREAK}${pack}`;
  } catch (_error) {
    return `${activity.game_id}-${PHONE_DETECTION_BREAK}${activity.pack_id}`;
  }
};

const formatOperator = (activity: AuditShiftActivityItem) => {
  return activity.operator_name || activity.created_by_name || '-';
};

const ShiftActivityLedger: React.FC<{
  activities: AuditShiftActivityItem[];
  barcodeFormat?: string | null;
}> = ({ activities, barcodeFormat }) => {
  if (!activities.length) {
    return null;
  }

  return (
    <section className="audit-detail-shift-activity" aria-label="Shift activity">
      <div className="audit-detail-shift-activity__title">Shift Activity</div>
      <div className="audit-detail-shift-activity__rows">
        {activities.map((activity) => {
          const gamePack = formatGamePackValue(activity, activity.barcode_format || barcodeFormat);
          const activityKey = activity.event_id || activity.activity_id || `${activity.created_at}-${activity.event_type}-${activity.game_id}-${activity.pack_id}`;

          return (
            <div className="audit-detail-shift-activity__row" key={activityKey}>
              <div className="audit-detail-shift-activity__time">{formatActivityTime(activity)}</div>
              <div className="audit-detail-shift-activity__action">{formatActivityLabel(activity)}</div>
              <div className="audit-detail-shift-activity__pack">
                {gamePack ? (
                  <>
                    Game{' '}
                    <span className="audit-detail-shift-activity__game-pack">{gamePack}</span>
                  </>
                ) : null}
                {activity.slot_id ? ` - Bin ${activity.slot_id}` : ''}
              </div>
              <div className="audit-detail-shift-activity__operator">By {formatOperator(activity)}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default ShiftActivityLedger;
